import React, { useState, useEffect } from 'react';
import { Briefcase, Plus, Loader2, Upload } from 'lucide-react';
import { Holding, subscribeToHoldings, removeHolding } from '../../services/holdingsService';
import { fetchWithDelay } from '../../lib/utils';
import { HoldingCardMobile } from './HoldingCardMobile';
import { AddPositionModal } from './AddPositionModal';
import { PerformanceChart } from './PerformanceChart';
import { ImportTransactionsModal } from './ImportTransactionsModal';
import { cn } from '../../lib/utils';

interface PortfolioMobileProps {
  uid: string;
  onAnalyzeTicker: (ticker: string) => void;
}

export function PortfolioMobile({ uid, onAnalyzeTicker }: PortfolioMobileProps) {
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [prices, setPrices] = useState<Record<string, number>>({});

  useEffect(() => {
    const unsubscribe = subscribeToHoldings(uid, (newHoldings) => {
      setHoldings(newHoldings);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [uid]);

  useEffect(() => {
    if (holdings.length === 0) return;
    
    fetchWithDelay(
      holdings,
      async (h) => {
        try {
          const res = await fetch(`/api/fmp/stable/quote?symbol=${h.ticker}`);
          if (res.ok) {
            const data = await res.json();
            if (data?.[0]?.price) return { ticker: h.ticker, price: data[0].price };
          }
        } catch {}
        return { ticker: h.ticker, price: null };
      },
      250
    ).then(results => {
      const map: Record<string, number | null> = {};
      results.forEach(r => { if (r) map[r.ticker] = r.price; });
      setPrices(map as Record<string, number>);
    });
  }, [holdings]);

  const handleRemove = async (ticker: string, docId?: string) => {
    try {
      await removeHolding(uid, ticker, docId);
    } catch (err) {
      console.error("Failed to remove holding", err);
    }
  };

  const handleKeeperComplete = (ticker: string, verdict: 'KEEP' | 'WATCH' | 'EXIT') => {
    setHoldings(prev => prev.map(h =>
      h.ticker === ticker ? { ...h, lastKeeperVerdict: verdict } : h
    ));
  };

  const keepCount = holdings.filter(h => h.lastKeeperVerdict === 'KEEP').length;
  const watchCount = holdings.filter(h => h.lastKeeperVerdict === 'WATCH').length;
  const exitCount = holdings.filter(h => h.lastKeeperVerdict === 'EXIT').length;
  const notAssessedCount = holdings.filter(h => !h.lastKeeperVerdict).length;
  const existingTickers = new Set(holdings.map(h => h.ticker));

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-ink-500" />
      </div>
    );
  }

  return (
    <div className="px-4 py-6 pb-28 space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif font-medium italic tracking-tight">Portfolio</h2>
          <p className="text-xs text-ink-500 font-medium mt-0.5">Assessed by The Keeper</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center justify-center gap-1.5 px-3 py-2 bg-surface-2 text-ink-600 rounded-md text-xs font-medium uppercase tracking-wider hover:bg-surface transition-colors"
          >
            <Upload className="w-3.5 h-3.5" />
            Import
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-oxblood text-paper rounded-md text-xs font-medium uppercase tracking-wider hover:bg-oxblood-800 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add
          </button>
        </div>
      </div>

      {/* Summary bar */}
      {holdings.length >= 3 && (
        <div className="flex flex-wrap gap-2 text-[10px] font-medium uppercase tracking-wider">
          <span className="text-ink-500">{holdings.length} positions</span>
          <span className="text-success-700">{keepCount} Keep</span>
          <span className="text-warning-700">{watchCount} Watch</span>
          <span className="text-danger-600">{exitCount} Exit</span>
          {notAssessedCount > 0 && (
            <span className="text-ink-500">{notAssessedCount} unassessed</span>
          )}
        </div>
      )}

      {/* Empty state */}
      {holdings.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center py-16 space-y-4">
          <div className="w-14 h-14 bg-surface-2 rounded-lg flex items-center justify-center">
            <Briefcase className="w-7 h-7 text-ink-300" />
          </div>
          <div>
            <p className="font-medium text-ink mb-1">No positions yet</p>
            <p className="text-sm text-ink-500 max-w-xs">
              Add your first holding to get started with The Keeper.
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-oxblood text-paper rounded-md text-xs font-medium uppercase tracking-wider hover:bg-oxblood-800 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Position
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="oi-card p-4">
            <h3 className="text-xs font-medium uppercase tracking-wider text-ink-500 mb-3">
              Performance vs S&P 500
            </h3>
            <PerformanceChart holdings={holdings} prices={prices} />
          </div>

          {holdings.map((holding) => (
            <HoldingCardMobile
              key={holding.ticker}
              holding={holding}
              uid={uid}
              onRemove={handleRemove}
              onAnalyze={onAnalyzeTicker}
              onKeeperComplete={handleKeeperComplete}
              currentPrice={prices[holding.ticker] ?? null}
            />
          ))}
        </div>
      )}

      {showAddModal && (
        <AddPositionModal
          uid={uid}
          onClose={() => setShowAddModal(false)}
          onAdded={() => setShowAddModal(false)}
        />
      )}

      {showImportModal && (
        <ImportTransactionsModal
          uid={uid}
          existingTickers={existingTickers}
          onClose={() => setShowImportModal(false)}
          onImported={() => setShowImportModal(false)}
        />
      )}
    </div>
  );
}
